import { Device } from '@twilio/voice-sdk'
import { toast } from 'react-toastify'
import {
  createMentorPortalVoiceBootstrap,
  renewMentorPortalVoiceCall,
} from 'src/mentor/services/portalVoiceCallService'

const listeners = new Set()

function buildIdleState() {
  return {
    status: 'idle',
    interactionId: null,
    bookedEventId: null,
    destinationType: null,
    destinationLabel: '',
    maskedDestination: '',
    recordingEnabled: false,
    renewalEnabled: false,
    renewalAllowed: false,
    renewalInFlight: false,
    renewalToken: null,
    renewalCount: 0,
    callTimeLimitSeconds: 0,
    renewalIncrementSeconds: 0,
    maxCallDurationSeconds: 0,
    callExpiresAtMs: null,
    remainingSeconds: 0,
    error: '',
  }
}

let currentState = buildIdleState()
let currentDevice = null
let currentConnection = null
let disconnectRequestedByUser = false
let countdownTimerId = null

function parseServerInstant(value) {
  if (!value) {
    return null
  }

  const directTimestamp = Date.parse(value)
  if (!Number.isNaN(directTimestamp)) {
    return directTimestamp
  }

  const utcTimestamp = Date.parse(`${value} UTC`)
  return Number.isNaN(utcTimestamp) ? null : utcTimestamp
}

function getRemainingSeconds(callExpiresAtMs) {
  if (!callExpiresAtMs) {
    return 0
  }
  return Math.max(0, Math.ceil((callExpiresAtMs - Date.now()) / 1000))
}

function syncCountdownTimer() {
  if (countdownTimerId) {
    clearInterval(countdownTimerId)
    countdownTimerId = null
  }

  if (currentState.status === 'idle' || !currentState.callExpiresAtMs) {
    return
  }

  countdownTimerId = window.setInterval(() => {
    if (currentState.status === 'idle') {
      syncCountdownTimer()
      return
    }

    const remainingSeconds = getRemainingSeconds(currentState.callExpiresAtMs)
    if (remainingSeconds !== currentState.remainingSeconds) {
      currentState = {
        ...currentState,
        remainingSeconds,
      }
      emitState()
    }
  }, 1000)
}

function emitState() {
  const snapshot = { ...currentState }
  listeners.forEach((listener) => {
    try {
      listener(snapshot)
    } catch (error) {
      console.error('Portal voice state listener failed:', error)
    }
  })
}

function setState(nextState) {
  currentState = {
    ...nextState,
    remainingSeconds: getRemainingSeconds(nextState?.callExpiresAtMs),
  }
  syncCountdownTimer()
  emitState()
}

function resetState() {
  disconnectRequestedByUser = false
  setState(buildIdleState())
}

function destroyDevice() {
  const activeConnection = currentConnection
  currentConnection = null

  if (activeConnection) {
    try {
      activeConnection.disconnect()
    } catch (error) {
      console.warn('Error disconnecting active portal voice call:', error)
    }
  }

  const activeDevice = currentDevice
  currentDevice = null

  if (activeDevice) {
    try {
      activeDevice.destroy()
    } catch (error) {
      console.warn('Error destroying Twilio device:', error)
    }
  }
}

function attachDeviceListeners(device) {
  device.on('error', (error) => {
    console.error('Portal voice device error:', error)
    toast.error(error?.message || 'The portal voice call failed to initialize.')
    destroyDevice()
    resetState()
  })
}

function attachConnectionListeners(connection, route) {
  connection.on('ringing', () => {
    setState({
      ...currentState,
      status: 'ringing',
      destinationLabel: route?.destinationLabel || route?.title || currentState.destinationLabel,
      maskedDestination: route?.maskedDestination || currentState.maskedDestination,
      error: '',
    })
  })

  connection.on('accept', () => {
    const expiresAtMs =
      currentState.callExpiresAtMs ||
      (route?.callTimeLimitSeconds ? Date.now() + route.callTimeLimitSeconds * 1000 : null)

    setState({
      ...currentState,
      status: 'connected',
      destinationLabel: route?.destinationLabel || route?.title || currentState.destinationLabel,
      maskedDestination: route?.maskedDestination || currentState.maskedDestination,
      callExpiresAtMs: expiresAtMs,
      error: '',
    })
    toast.success(
      `Connected to ${route?.destinationLabel || route?.title || 'the participant'}${
        route?.maskedDestination ? ` (${route.maskedDestination})` : ''
      }.`,
    )
  })

  connection.on('cancel', () => {
    toast.info('The portal voice call was cancelled.')
    destroyDevice()
    resetState()
  })

  connection.on('reject', () => {
    toast.info('The portal voice call was rejected.')
    destroyDevice()
    resetState()
  })

  connection.on('disconnect', () => {
    if (disconnectRequestedByUser) {
      toast.info('The portal voice call ended.')
    }
    destroyDevice()
    resetState()
  })

  connection.on('error', (error) => {
    console.error('Portal voice connection error:', error)
    toast.error(error?.message || 'The portal voice call ended with an error.')
    destroyDevice()
    resetState()
  })
}

export function getPortalVoiceCallState() {
  return { ...currentState }
}

export function subscribePortalVoiceCallState(listener) {
  if (typeof listener !== 'function') {
    return () => {}
  }

  listeners.add(listener)
  listener(getPortalVoiceCallState())
  return () => {
    listeners.delete(listener)
  }
}

export async function startPortalVoiceCall({ bookedEventId, destinationType, uiSurface }) {
  if (currentState.status !== 'idle') {
    throw new Error('A portal voice call is already in progress.')
  }

  const bootstrap = await createMentorPortalVoiceBootstrap({
    bookedEventId,
    destinationType,
    uiSurface,
  })

  if (!bootstrap?.ok) {
    throw new Error(bootstrap?.message || 'Unable to prepare the portal voice call.')
  }

  const route = bootstrap?.route || {}
  setState({
    status: 'connecting',
    interactionId: bootstrap?.callSessionId || null,
    bookedEventId,
    destinationType,
    destinationLabel: route?.destinationLabel || route?.title || 'Portal Call',
    maskedDestination: route?.maskedDestination || '',
    recordingEnabled: !!route?.recordingEnabled,
    renewalEnabled: !!route?.renewalEnabled,
    renewalAllowed: !!route?.renewalEnabled,
    renewalInFlight: false,
    renewalToken: bootstrap?.renewalToken || null,
    renewalCount: 0,
    callTimeLimitSeconds: route?.callTimeLimitSeconds || 0,
    renewalIncrementSeconds: route?.renewalIncrementSeconds || 0,
    maxCallDurationSeconds: route?.maxCallDurationSeconds || 0,
    callExpiresAtMs: null,
    error: '',
  })

  try {
    currentDevice = new Device(bootstrap.accessToken, {
      logLevel: 1,
      codecPreferences: ['opus', 'pcmu'],
      closeProtection: false,
    })
    attachDeviceListeners(currentDevice)

    currentConnection = await currentDevice.connect({
      params: {
        requestToken: bootstrap.requestToken,
      },
    })
    attachConnectionListeners(currentConnection, route)
  } catch (error) {
    console.error('Error starting portal voice call:', error)
    destroyDevice()
    resetState()
    throw error
  }

  return {
    ok: true,
    route,
  }
}

export async function extendPortalVoiceCall() {
  if (currentState.status === 'idle') {
    throw new Error('There is no active portal voice call to extend.')
  }
  if (!currentState.renewalAllowed || !currentState.renewalEnabled) {
    throw new Error('This portal voice call cannot be extended.')
  }
  if (!currentState.interactionId || !currentState.renewalToken) {
    throw new Error('A renewal token is not available for this call.')
  }
  if (currentState.renewalInFlight) {
    throw new Error('A call extension is already in progress.')
  }

  const stateSnapshot = getPortalVoiceCallState()
  setState({
    ...stateSnapshot,
    renewalInFlight: true,
    error: '',
  })

  const renewalResult = await renewMentorPortalVoiceCall({
    interactionId: stateSnapshot.interactionId,
    renewalToken: stateSnapshot.renewalToken,
  })

  if (!renewalResult?.ok) {
    setState({
      ...getPortalVoiceCallState(),
      renewalInFlight: false,
      error: renewalResult?.message || 'Unable to extend the portal voice call.',
    })
    throw new Error(renewalResult?.message || 'Unable to extend the portal voice call.')
  }

  const expiresAtMs =
    parseServerInstant(renewalResult?.callExpiresAt) ||
    (renewalResult?.remainingSeconds
      ? Date.now() + renewalResult.remainingSeconds * 1000
      : stateSnapshot.callExpiresAtMs)

  setState({
    ...getPortalVoiceCallState(),
    renewalInFlight: false,
    renewalAllowed: !!renewalResult?.renewalAllowed,
    renewalToken: renewalResult?.renewalToken || null,
    renewalCount: renewalResult?.renewalCount ?? stateSnapshot.renewalCount,
    callTimeLimitSeconds: renewalResult?.callTimeLimitSeconds || stateSnapshot.callTimeLimitSeconds,
    renewalIncrementSeconds:
      renewalResult?.renewalIncrementSeconds || stateSnapshot.renewalIncrementSeconds,
    maxCallDurationSeconds:
      renewalResult?.maxCallDurationSeconds || stateSnapshot.maxCallDurationSeconds,
    callExpiresAtMs: expiresAtMs,
    error: '',
  })

  const extensionMinutes = Math.max(
    1,
    Math.round((renewalResult?.renewalIncrementSeconds || stateSnapshot.renewalIncrementSeconds || 0) / 60),
  )
  toast.success(`Extended the portal voice call by ${extensionMinutes} minute${extensionMinutes === 1 ? '' : 's'}.`)

  return {
    ok: true,
    ...renewalResult,
  }
}

export function endPortalVoiceCall() {
  if (currentState.status === 'idle') {
    return
  }

  disconnectRequestedByUser = true

  if (currentConnection) {
    try {
      currentConnection.disconnect()
      return
    } catch (error) {
      console.warn('Error disconnecting portal voice connection:', error)
    }
  }

  if (currentDevice) {
    try {
      currentDevice.destroy()
    } catch (error) {
      console.warn('Error destroying Twilio device after portal voice disconnect:', error)
    }
  }

  resetState()
}
